import React, { PureComponent, ReactNode } from 'react';

import qs from 'qs';
import { FormattedMessage } from 'react-intl';

import ButtonGroup from '@atlaskit/button/button-group';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import { Grid, GridColumn } from '@atlaskit/page';
import { getCloneUrl, CloneProtocol } from '@atlassian/bitkit-clone-controls';

import { Repository } from 'src/components/types';
import emptyStateImgUrl from 'src/sections/dashboard/sections/overview/components/empty-folder.svg';
import sourceUrls from 'src/urls/source';

import repoUrls from '../urls';

import CloneCommand from './clone-command';
import messages from './empty-state.i18n';
import * as styles from './empty-state.style';

const getRemoteAddCommand = (
  repository: Repository,
  protocol: CloneProtocol
) => {
  const url = getCloneUrl(protocol, repository);

  if (!url) {
    return null;
  }

  return `git remote add origin ${url}`;
};

export const createNewFileUrl = (repository: Repository, filename: string) => {
  const nextUrl = sourceUrls.ui.source(repository.full_name);
  const nextUrlQuery = qs.stringify({ init: true });

  const createFileUrl = sourceUrls.ui.createFile(repository.full_name);
  const createFileUrlQuery = qs.stringify({
    filename,
    template: true,
    next: `${nextUrl}?${nextUrlQuery}`,
  });

  return `${createFileUrl}/?${createFileUrlQuery}`;
};

type SectionProps = {
  children: ReactNode;
  medium: number;
};

const Section = (props: SectionProps) => (
  <styles.EmptyStateSection>
    <Grid>
      <GridColumn />
      <GridColumn medium={props.medium}>{props.children}</GridColumn>
      <GridColumn />
    </Grid>
  </styles.EmptyStateSection>
);

export type EmptyStateProps = {
  hasSshKey: boolean;
  repository: Repository;
};

export type EmptyStateState = {
  isLeaveRepoModalOpen: boolean;
};

export default class EmptyState extends PureComponent<
  EmptyStateProps,
  EmptyStateState
> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    hasSshKey: false,
  };

  render() {
    const { hasSshKey, repository } = this.props;

    const mainBranchName = repository.mainbranch?.name || 'master';
    const protocol = hasSshKey ? 'ssh' : 'https';
    const ignoreFileName = '.gitignore';
    const remoteAddCommand = getRemoteAddCommand(repository, protocol);
    const remotePushCommand = `git push -u origin ${mainBranchName}`;

    return (
      <div>
        <Section medium={8}>
          <styles.CenteredColumn>
            <styles.ImageContainer>
              <img src={emptyStateImgUrl} alt="" role="presentation" />
            </styles.ImageContainer>
            <styles.EmptyStateHeader data-qa="empty-state__header">
              <FormattedMessage {...messages.header} />
            </styles.EmptyStateHeader>
            <CloneCommand initialProtocol={protocol} repository={repository} />
          </styles.CenteredColumn>
        </Section>

        <Section medium={10}>
          <FormattedMessage tagName="h2" {...messages.getStartedHeader} />
          <styles.Instructions>
            <FormattedMessage
              {...messages.getStartedInstructions}
              values={{ ignoreFileName }}
            />
          </styles.Instructions>
          <ButtonGroup>
            <CustomThemeButton href={createNewFileUrl(repository, 'README.md')}>
              <FormattedMessage {...messages.createReadme} />
            </CustomThemeButton>
            <CustomThemeButton
              href={createNewFileUrl(repository, ignoreFileName)}
            >
              <FormattedMessage
                {...messages.createGitIgnore}
                values={{ ignoreFileName }}
              />
            </CustomThemeButton>
          </ButtonGroup>

          <FormattedMessage
            tagName="h2"
            {...messages.localRepositoryHeaderGit}
          />

          <FormattedMessage
            tagName="p"
            {...messages.localRepositoryInstructionsStep1}
          />
          <styles.Code>
            <styles.CodeLine>cd /path/to/your/repo</styles.CodeLine>
          </styles.Code>
          <FormattedMessage
            tagName="p"
            {...messages.localRepositoryInstructionsStep2}
          />
          {remoteAddCommand && (
            <styles.Code>
              <styles.CodeLine>{remoteAddCommand}</styles.CodeLine>
              <styles.CodeLine>{remotePushCommand}</styles.CodeLine>
            </styles.Code>
          )}
          <FormattedMessage
            tagName="p"
            {...messages.localRepositoryInstructionsMoreInfo}
            values={{
              learnHowLink: (
                <a
                  href={repoUrls.external.emptyStateLearnMore}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage
                    {...messages.localRepositoryInstructionsLearnMoreLink}
                  />
                </a>
              ),
            }}
          />
        </Section>
      </div>
    );
  }
}
